var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
import * as React from 'react';
import { CredoOptionInput } from "../__generated__/graphql-global-types";
var CredoStateContext = React.createContext(undefined);
var CredoDispatchContext = React.createContext(undefined);
var generateDefaultResponses = function (credoTopics) { return credoTopics.map(function (topic) { return ({
    answers: topic.questions.map(function (q) { return ({
        id: q.id,
        option: q.options.find(function (o) { return (o.option === CredoOptionInput.YES); }) ?
            CredoOptionInput.YES : q.options[0].option,
        error: (q.options.find(function (o) { return (o.option === CredoOptionInput.YES); }) || { details: undefined })
            .details ? 'Details Required' : null,
        details: null
    }); })
}); }); };
var generateDefaultParentTicketDetails = function () { return ({ ticketKey: "NA", summary: "NA", ticketStatus: "NA", tier: -1 }); };
var generateCredoTopics = function (credoTopics, tier) {
    return credoTopics.reduce(function (ret, _a) {
        var title = _a.title, version = _a.version, questions = _a.questions, __typename = _a.__typename;
        var filtered = questions.reduce(function (ret, q) { return q.tier >= tier ? __spreadArrays(ret, [q]) : ret; }, []);
        return filtered.length ? __spreadArrays(ret, [{
                __typename: __typename,
                title: title,
                version: version,
                questions: filtered,
            }]) : ret;
    }, []);
};
var credoReducer = function (state, action) {
    switch (action.type) {
        case 'setTier': {
            var credoTopics = generateCredoTopics(state.staticCredoTopics, action.data.tier);
            var credoResponses = generateDefaultResponses(credoTopics);
            return __assign(__assign({}, state), { tier: action.data.tier, credoTopics: credoTopics,
                credoResponses: credoResponses });
        }
        case 'setServiceTemplate': {
            return __assign(__assign({}, state), { serviceTemplate: action.data.serviceTemplate });
        }
        case 'setSoc2': {
            return __assign(__assign({}, state), { soc2: action.data.soc2 });
        }
        case 'setAnswer': {
            var newState = __assign({}, state);
            // set question answer
            newState.credoResponses[action.data.stepId].answers[action.data.questionId].option = action.data.option;
            // check if answer requires details
            var reqDetails = (newState.credoTopics[action.data.stepId].questions[action.data.questionId]
                .options.find(function (o) { return (o.option === action.data.option); }) || { details: undefined }).details;
            var details = newState.credoResponses[action.data.stepId].answers[action.data.questionId].details;
            // set error if details are required but not provided
            newState.credoResponses[action.data.stepId].answers[action.data.questionId].error = reqDetails && !details ? 'Details Required' : null;
            return newState;
        }
        case 'setDetails': {
            var newState = __assign({}, state);
            if (action.data.details) {
                newState.credoResponses[action.data.stepId].answers[action.data.questionId].details = action.data.details;
                newState.credoResponses[action.data.stepId].answers[action.data.questionId].error = null;
            }
            else {
                newState.credoResponses[action.data.stepId].answers[action.data.questionId].details = null;
                newState.credoResponses[action.data.stepId].answers[action.data.questionId].error = 'Details Required';
            }
            return newState;
        }
        case 'setWorkflowId': {
            return __assign(__assign({}, state), { workflowId: action.data.workflowId });
        }
        case 'setStep': {
            return __assign(__assign({}, state), { step: action.data.value });
        }
        case 'setPage': {
            return __assign(__assign({}, state), { page: action.data.value });
        }
        case 'resetCredoResponses': {
            return __assign(__assign({}, state), { credoResponses: generateDefaultResponses(state.credoTopics) });
        }
        case 'markCredoProcessComplete': {
            return __assign(__assign({}, state), { credoCompleteStage: false, credoOnGoingTicketDetails: [], credoOnGoingParentTicketDetails: generateDefaultParentTicketDetails() });
        }
        case 'setCredoCompleteStage': {
            return __assign(__assign({}, state), { credoCompleteStage: action.data.value });
        }
        default: {
            throw new Error("Unhandled action: " + action);
        }
    }
};
// useLocallyPersistedReducer uses localStorage to save react state between usages
// NOTE: it does not handle schema changing of the defaultState; update CREDO_STORAGE_KEY version
var useLocallyPersistedReducer = function (reducer, defaultState, storageKey) {
    var hookVars = React.useReducer(reducer, defaultState, function (defaultState) {
        var persisted = JSON.parse(localStorage.getItem(storageKey) || '{}');
        return Object.keys(persisted).length === 0 && persisted.constructor === Object
            ? defaultState
            : persisted;
    });
    if (defaultState.credoCompleteStage) {
        hookVars[0].credoCompleteStage = defaultState.credoCompleteStage;
        hookVars[0].credoOnGoingParentTicketDetails = defaultState.credoOnGoingParentTicketDetails;
        hookVars[0].credoOnGoingTicketDetails = defaultState.credoOnGoingTicketDetails;
        hookVars[0].page = defaultState.page;
        hookVars[0].step = defaultState.step;
    }
    else if (hookVars[0].page === 5) {
        // when credo process is completed from outside, due to the fact that local storage has information about page number,
        // even though completeCredoStage is false, page and step attributes wouldn't have updated, hence this extra check is required.
        hookVars[0].page = 0;
        hookVars[0].step = 0;
    }
    hookVars[0].tier = defaultState.tier;
    hookVars[0].soc2 = defaultState.soc2;
    React.useEffect(function () {
        localStorage.setItem(storageKey, JSON.stringify(hookVars[0]));
    }, [storageKey, hookVars[0]]);
    return hookVars;
};
var CredoProvider = function (_a) {
    var children = _a.children, tier = _a.tier, serviceId = _a.serviceId, soc2 = _a.soc2, staticCredoTopics = _a.staticCredoTopics, credoCompleteStage = _a.credoCompleteStage, credoOnGoingTicketDetails = _a.credoOnGoingTicketDetails, credoOnGoingParentTicketDetails = _a.credoOnGoingParentTicketDetails;
    var credoTopics = generateCredoTopics(staticCredoTopics, tier);
    var credoResponses = generateDefaultResponses(credoTopics);
    var serviceTemplate = true;
    var page = credoCompleteStage ? 5 : 0;
    var step = credoCompleteStage ? 5 : 0;
    // write the cache to a new key when the topic version changes
    var credoStorageKey = credoTopics[0].version + "-credoState-" + serviceId;
    // NOTE: when modifying defaultState, you need to bump the version of CREDO_STORAGE_KEY
    var defaultState = {
        serviceTemplate: serviceTemplate,
        tier: tier,
        soc2: soc2,
        serviceId: serviceId,
        staticCredoTopics: staticCredoTopics,
        credoTopics: credoTopics,
        credoResponses: credoResponses,
        page: page,
        step: step,
        credoCompleteStage: credoCompleteStage,
        credoOnGoingTicketDetails: credoOnGoingTicketDetails,
        credoOnGoingParentTicketDetails: credoOnGoingParentTicketDetails
    };
    // save the state per serviceId with the version + serviceId
    var _b = useLocallyPersistedReducer(credoReducer, defaultState, credoStorageKey), state = _b[0], dispatch = _b[1];
    return (React.createElement(CredoStateContext.Provider, { value: state },
        React.createElement(CredoDispatchContext.Provider, { value: dispatch }, children)));
};
var useCredoState = function () {
    var context = React.useContext(CredoStateContext);
    if (context === undefined) {
        throw new Error('useCredoState must be used within a CredoProvider');
    }
    return context;
};
var useCredoDispatch = function () {
    var context = React.useContext(CredoDispatchContext);
    if (context === undefined) {
        throw new Error('useCredoDispatch must be used within a CredoProvider');
    }
    return context;
};
export { CredoProvider, useCredoState, useCredoDispatch };
